import { analyticsManager } from '../core'
import type { EventName, EventDataFor } from './types/events'
import type { AutoDimensions } from './types/dimensions'
import { TrackEventOptions } from '../core/types/core'
import {
  buildProductName,
  gaSearchTypeMap,
  getSelectedProductQtyAvailable,
} from './utils'
import { NonCachedSearchType, ProductModel } from 'interfaces'
import { FilterGroupName, ProductCatalogInfo } from 'results'
import { Filters } from '../../../store/models/SearchModels'

class AnalyticsTracker {
  private static instance: AnalyticsTracker

  private constructor() {
    //
  }

  public static getInstance(): AnalyticsTracker {
    if (!AnalyticsTracker.instance) {
      AnalyticsTracker.instance = new AnalyticsTracker()
    }
    return AnalyticsTracker.instance
  }

  public setVehicle(vehicle: {
    year?: string
    make?: string
    model?: string
    engine?: string
    id?: string
  }): void {
    // Domain-specific validation
    if (
      !vehicle.year ||
      !vehicle.make ||
      !vehicle.model ||
      !vehicle.engine ||
      !vehicle.id
    ) {
      // Don't log error for Misc vehicle.
      if (vehicle.make !== 'MISCELLANEOUS') {
        // eslint-disable-next-line no-console
        console.error('Invalid vehicle info provided')
      }
      return
    }

    analyticsManager.setVehicle({
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      engine: vehicle.engine,
      id: vehicle.id,
    })
  }

  public clearVehicle(): void {
    analyticsManager.clearVehicle()
  }

  public setUser(userId: string, domain?: string): void {
    analyticsManager.setUser(userId, domain)
  }

  public clearUser(): void {
    analyticsManager.clearUser()
  }

  public setGlobalContext(context: AutoDimensions): void {
    analyticsManager.setGlobalContext(context)
  }

  public clearGlobalContext(keys?: string[]): void {
    analyticsManager.clearGlobalContext(keys)
  }

  public trackEvent<T extends EventName>(
    eventName: T,
    eventData: Omit<EventDataFor<T>, 'event'>,
    options?: TrackEventOptions
  ): void {
    analyticsManager.trackEvent(
      eventName,
      {
        ...eventData,
        event_category: this.getEventCategory(eventName),
      },
      options
    )
  }

  public pageView = (pageName: string): void => {
    this.trackEvent('page_view', {
      page_title: pageName,
      page_location: pageName,
      page_path: pageName,
    })
  }

  public buildTheProductListName = (
    searchType: NonCachedSearchType,
    searchTerm?: string,
    vehicleSystem?: string,
    catalogInfo?: ProductCatalogInfo
  ): string => {
    try {
      const gaSearchType = gaSearchTypeMap?.get(searchType) || ''
      const category = catalogInfo?.category ? `/${catalogInfo.category}` : ''
      const group = catalogInfo?.group ? `/${catalogInfo.group}` : ''
      const partType = catalogInfo?.partType ? `/${catalogInfo.partType}` : ''
      const system = vehicleSystem ? `/${vehicleSystem}` : ''

      if (searchType === NonCachedSearchType.searchByNavigation)
        return `${gaSearchType}${category}${group}${partType}`

      if (searchType === NonCachedSearchType.searchByAstNavigation)
        return `${gaSearchType}${category}${group}${partType}`

      if (searchType === NonCachedSearchType.searchGfxByNavigation)
        return `${gaSearchType}${category}${group}${system}`

      if (searchType === NonCachedSearchType.searchBySpecificParts)
        return `${gaSearchType}`

      if (searchType === NonCachedSearchType.searchBySaytCategories)
        return `${gaSearchType}/${searchTerm}`

      return `${gaSearchType}/${searchTerm}`
    } catch (_e) {
      return ''
    }
  }

  public trackSearchResults = (
    results: ProductModel[],
    searchType: NonCachedSearchType,
    getCatalogInfo: (product: ProductModel) => ProductCatalogInfo,
    searchTerm?: string,
    vehicleSystem?: string,
    externalSeller?: boolean,
    filters?: Filters
  ): void => {
    let gaList = []

    try {
      gaList =
        results && Array.isArray(results)
          ? results
              .map((product, idx) => {
                try {
                  const catalogInfo = getCatalogInfo?.(product)
                  const productListName =
                    this.buildTheProductListName?.(
                      searchType,
                      searchTerm,
                      vehicleSystem,
                      catalogInfo
                    ) || ''

                  return {
                    item_id:
                      `${product?.lineCode || ''} ${product?.partNumber || ''}`.trim(),
                    item_name: buildProductName?.(product) || '',
                    item_brand:
                      product?.brandName ?? product?.manufacturerName ?? '',
                    item_list_name: productListName,
                    item_list_id: gaSearchTypeMap?.get(searchType),
                    item_category:
                      catalogInfo?.partType || product?.description, // Added product.description by refering the add_to_cart event
                    list_position: idx + 1,
                    quantity: getSelectedProductQtyAvailable?.(product),
                    price: product?.location?.[0]?.cost,
                    external_seller: Boolean(externalSeller),
                    affiliation: product?.location?.[0]?.called,
                  }
                } catch (_e) {
                  return null
                }
              })
              .filter((item) => item !== null)
          : []
    } catch (_e) {
      gaList = []
    }

    try {
      this.trackEvent?.('view_item_list', {
        item_list_id: gaSearchTypeMap?.get(searchType),
        brand_filter:
          filters
            .find((f) => f.groupName === FilterGroupName.BRANDS)
            ?.items.join(',') ?? '',
        part_filter:
          filters
            .find((f) => f.groupName === FilterGroupName.PART_TYPE)
            ?.items.join(',') ?? '',
        items: gaList,
      })
    } catch (_e) {
      // Prevents ad blockers from making the site unusable
    }
  }

  private getEventCategory(eventName: EventName): string {
    switch (eventName) {
      case 'search':
        return 'search'
      case 'view_item':
      case 'view_item_list':
      case 'add_to_cart':
      case 'remove_from_cart':
      case 'begin_checkout':
      case 'purchase':
        return 'ecommerce'
      default:
        return 'general'
    }
  }
}

export const TrackingManager = AnalyticsTracker.getInstance()
